import { ref, onMounted, onUnmounted } from "vue";
import { useAuthStore, useUIStore } from "@/stores";

export function useTurnstile(elementId = "turnstile", options = {}) {
  const authStore = useAuthStore();
  const UIStore = useUIStore();
  const { persist = false } = options; // New option

  const is_ready = ref(false);
  const widget_id = ref(null);
  const error = ref(null);

  const sitekey =
    import.meta.env.VITE_ENV === "Production"
      ? "0x4AAAAAAAbuzA10_ip9GLl-"
      : "1x00000000000000000000AA";

  const initTurnstile = () => {
    if (typeof turnstile === "undefined") {
      console.warn("Turnstile script not loaded");
      error.value = "Turnstile script not loaded";
      return;
    }

    if (widget_id.value) return; // Already rendered

    turnstile.ready(() => {
      is_ready.value = true;

      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Turnstile element #${elementId} not found`);
        error.value = "Turnstile element not found";
        return;
      }

      // Check if element already has a Turnstile widget
      if (element.querySelector("iframe")) {
        console.warn("Turnstile widget already exists, cleaning up...");
        element.innerHTML = "";
      }

      widget_id.value = turnstile.render(`#${elementId}`, {
        sitekey,
        // theme: UIStore.is_dark_mode ? "dark" : "light",
        callback: (token) => {
          authStore.turnstile_token = token;
          authStore.is_turnstile_valid = true;
          error.value = null;
        },
        "error-callback": () => {
          authStore.is_turnstile_valid = false;
          error.value = "Turnstile verification failed";
        },
        "expired-callback": () => {
          authStore.is_turnstile_valid = false;
          error.value = "Turnstile expired";
        },
      });
    });
  };

  const resetTurnstile = () => {
    if (typeof turnstile === "undefined" || !widget_id.value) return;

    turnstile.reset(widget_id.value);
    authStore.is_turnstile_valid = false;
    authStore.turnstile_token = "";
    error.value = null;
  };

  const cleanup = () => {
    const element = document.getElementById(elementId);

    // Remove via Turnstile API
    if (typeof turnstile !== "undefined" && widget_id.value) {
      try {
        turnstile.remove(widget_id.value);
      } catch (e) {
        console.warn("Error removing turnstile:", e);
      }
    }

    // Also clear the DOM element completely
    if (element) {
      element.innerHTML = "";
    }

    // Only clear auth store if NOT persisting
    if (!persist) {
      authStore.is_turnstile_valid = false;
      authStore.turnstile_token = "";
    }

    widget_id.value = null;
    is_ready.value = false;
  };

  onMounted(() => {
    initTurnstile();
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    is_ready,
    error,
    is_valid: () => authStore.is_turnstile_valid,
    initTurnstile,
    resetTurnstile,
    cleanup,
  };
}
